import React, { useState, useEffect, useRef, useCallback } from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useParams } from 'react-router-dom';
import { clone } from 'ramda';
import debounce from 'lodash.debounce';
import { Cropper } from 'react-cropper';
import {
  Button,
  Checkbox,
  Drawer,
  TextInputWithLabel,
  PopupMenu,
  DatePicker,
  FileUploader,
  Loader
} from 'quantux-ui';
import WarningDialog from '../warning-dialog';
import PayerCard from './payer-card';
import { blue, gray, salmon } from '../../theme';
import { BASE_URL, titleCase } from '../../utils';

const siIcon = (
  <svg
    width="24"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Zm12.071-2L3.5 4.857v8.572A8.57 8.57 0 0 0 12.071 22a8.57 8.57 0 0 0 8.572-8.571V4.857L12.07 2Zm.003 18.393a6.965 6.965 0 0 0 6.964-6.964V6.063l-6.964-2.456-6.965 2.456v7.366a6.965 6.965 0 0 0 6.965 6.964Zm1.101-5.318V16h-4v-.925h1.465V10.86c0-.167.005-.338.015-.515l-1.04.87a.456.456 0 0 1-.18.09.457.457 0 0 1-.3-.05.318.318 0 0 1-.09-.085l-.39-.535L10.86 8.76h1.015v6.315h1.3Zm2.957-8.095a2.222 2.222 0 0 0-.828-.148c-.307 0-.585.05-.836.148-.25.099-.465.239-.644.42a1.906 1.906 0 0 0-.412.664 2.526 2.526 0 0 0-.144.876c0 .325.048.619.144.88.099.261.236.484.412.668.179.184.393.325.644.424.25.099.53.148.836.148.304 0 .58-.05.828-.148.25-.099.464-.24.64-.424a1.87 1.87 0 0 0 .412-.668c.096-.261.144-.555.144-.88 0-.323-.048-.615-.144-.876a1.794 1.794 0 0 0-1.052-1.084Zm-.072 2.976c-.163.23-.415.344-.756.344-.347 0-.604-.115-.772-.344-.165-.232-.248-.568-.248-1.008 0-.44.083-.776.248-1.008.168-.235.425-.352.772-.352.341 0 .593.116.756.348.165.232.248.57.248 1.012 0 .443-.083.779-.248 1.008Z"
      fill="#96C8FF"
    />
  </svg>
);

const payerIcon = (
  <svg
    width="64"
    height="64"
    style={{ minWidth: '64px', minHeight: '64px' }}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 4a4 4 0 0 1 4-4h56a4 4 0 0 1 4 4v56a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Zm32.195 1.333L9.337 12.952v22.857c0 12.625 10.233 22.857 22.858 22.857S55.052 48.434 55.052 35.81V12.952l-22.857-7.62Zm.001 49.048c10.256 0 18.572-8.316 18.572-18.572V16.166L32.196 9.62l-18.571 6.547V35.81c0 10.256 8.316 18.572 18.571 18.572Z"
      fill="#00A0FF"
    />
  </svg>
);

const TODAY = new Date();

const SecondaryInsurance = (props) => {
  const [showInsurnaceDrawer, setShowInsurnaceDrawer] = useState(false);
  const [isNoSecondaryInsurance, setIsNoSecondaryInsurance] = useState(props.patientData.isNoSecondaryInsurance);
  // Primary Insurance Drawer
  const [payerSearch, setPayerSearch] = useState('');
  const [showPayerSearchMenu, setShowPayerSearchMenu] = useState(false);
  const [showDrawerCloseWarning, setShowDrawerCloseWarning] = useState(false);
  const [showPayerDeleteWarning, setShowPayerDeleteWarning] = useState(false);
  const [payerSearchResults, setPayerSearchResults] = useState([]);
  const [siData, setSiData] = useState({});
  //
  const [memberFirstName, setMemberFirstName] = useState('');
  const [memberMiddleName, setMemberMiddleName] = useState('');
  const [memberLastName, setMemberLastName] = useState('');
  const [isMemberDobPristine, setIsMemberDobPristine] = useState(true);
  const [memberDobDate, setMemberDobDate] = useState(TODAY.getDate());
  const [memberDobMonth, setMemberDobMonth] = useState(TODAY.getMonth());
  const [memberDobYear, setMemberDobYear] = useState(TODAY.getFullYear());
  const [patientRelationToMember, setPatientRelationToMember] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [authorizationNumber, setAuthorizationNumber] = useState('');
  const [groupNumber, setGroupNumber] = useState('');
  const [showRelationMenu, setShowRelationMenu] = useState(false);
  const [selectedPayer, setSelectedPayer] = useState({});
  // Edit Primary Insurance Drawer
  const [memberDataCopy, setMemberDataCopy] = useState({});
  const [selectedPayerCopy, setSelectedPayerCopy] = useState({});
  // Insruance Card Cropper Drawer - Front
  const [showFrontCropperDrawer, toggleFrontCropperDrawer] = useState(false);
  const [siFrontImageUrl, setSiFrontImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showFrontCropper, setShowFrontCropper] = useState(false);
  const [isFrontUploading, setIsFrontUploading] = useState(false);
  const [showFrontLoader, setShowFrontLoader] = useState(false);
  // Insruance Card Cropper Drawer - Back
  const [showBackCropperDrawer, toggleBackCropperDrawer] = useState(false);
  const [siBackImageUrl, setSiBackImageUrl] = useState('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
  const [showBackCropper, setShowBackCropper] = useState(false);
  const [isBackUploading, setIsBackUploading] = useState(false);
  const [showBackLoader, setShowBackLoader] = useState(false);
  //
  const [isSaved, setIsSaved] = useState(true);
  // Refs
  const payerSearchMenuRef = useRef();
  const relationRef = useRef();
  // Front card refs
  const frontCardRef = useRef();
  const frontCropperRef = useRef();
  const frontFileInputRef = useRef();
  // Back card refs
  const backCardRef = useRef();
  const backCropperRef = useRef();
  const backFileInputRef = useRef();

  // React Router
  const { patientId } = useParams();

  // == == == == Network Requests == == == ==
  const searchPayers = (value) => {
    const formData = new FormData();
    formData.append('searchTerm', value);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/payers2/searchPayersByName`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log(res.payers);
          setPayerSearchResults(res.payers);
          setShowPayerSearchMenu(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const loadSiData = () => {
    const url = new URL(`${BASE_URL}/patients/getPatientSecondaryInsuranceData`);
    const params = { patientId };
    url.search = new URLSearchParams(params).toString();

    fetch(url)
      .then(response => response.text())
      .then(result => {
        const reqData = JSON.parse(result);
        console.log('-------- Secondary Insurance');
        console.log(reqData);
        setSiData(reqData);

        if (reqData.payerData && Object.keys(reqData.payerData).length > 0) {
          console.log(reqData.payerData);
          setSelectedPayer(reqData.payerData);
        }

        if (reqData.siData && Object.keys(reqData.siData).length > 0) {
          const { siData } = reqData;
          // const localDob = new Date(siData.memberDob);
          let localDob;
          if (siData.memberDob) {
            localDob = new Date(siData.memberDob);
            setIsMemberDobPristine(false);
          } else {
            setIsMemberDobPristine(true);
            localDob = new Date();
          }
          setMemberDobMonth(localDob.getMonth());
          setMemberDobDate(localDob.getDate());
          setMemberDobYear(localDob.getFullYear());
          setAuthorizationNumber(siData.authorizationNumber);
          setGroupNumber(siData.groupNumber);
          setMemberFirstName(siData.memberFirstName);
          setMemberMiddleName(siData.memberMiddleName);
          setMemberLastName(siData.memberLastName);
          setPatientRelationToMember(siData.patientRelationToMember || '');
          setPolicyNumber(siData.policyNumber);
          setSiFrontImageUrl(siData.siCardFrontImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
          setSiBackImageUrl(siData.siCardBackImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
          setIsNoSecondaryInsurance(siData.isNoSecondaryInsurance);
        }
      })
      .catch(error => console.log('error', error));
  };

  const debouncedPayerssSearch = useCallback(debounce(value => searchPayers(value), 800), []);

  // == == == == Effects == == == ==
  useEffect(() => {
    if (payerSearch.length > 0) {
      debouncedPayerssSearch(payerSearch);
    } else if (payerSearch.length === 0) {
      setPayerSearchResults([]);
    }
  }, [payerSearch]);

  useEffect(() => {
    loadSiData();
  }, []);

  // == == == == Styles == == == ==
  const styles = StyleSheet.create({
    btnIcon: {
      fill: blue[800],
      ':not(.disabled):hover': {
        fill: blue[100]
      }
    },
    sideDrawerContainer: {
      // background: gray[600],
      background: '#002448',
      color: gray[300],
      height: '100%',
      position: 'relative',
      display: 'flex',
      flexFlow: 'column'
    },
    sideDrawerHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px',
      fontWeight: 'bold',
      borderBottom: '1px solid black',
      flexShrink: '0'
    },
    sideDrawerHeaderCloseIcon: {
      color: gray[300],
      ':hover': {
        color: salmon[500]
      }
    },
    sideDrawerBanner: {
      position: 'relative',
      padding: '8px',
      flexShrink: '0'
    },
    sideDrawerBannerImg: {
      width: '100%',
      height: 'auto'
    },
    sideDrawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerContentArea: {
      // height: 'calc(100vh - 186px)',
      // height: `${window.innerHeight - 170}px`,
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    sideDrawerUserCardAvatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '80px',
      height: '80px',
      color: 'white',
      borderRadius: '4px',
      position: 'relative'
    },
    sideDrawerUserCardAvatarImg: {
      width: 'auto',
      height: '80px',
      maxHeight: '80px'
    },
    sideDrawerAvatarCamera: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      left: '-3px',
      top: '58px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    },
    sideDrawerUserCardText: {
      fontSize: '12px'
    },
    profileTextAreaContainer: {
      paddingRight: '8px'
    },
    profileTextArea: {
      width: '100%',
      background: gray[900],
      height: '100px',
      color: gray[100],
      border: 'none',
      resize: 'none',
      overflow: 'auto',
      fontFamily: 'inherit',
      ':focus': {
        outlineColor: 'transparent',
        outlineStyle: 'none'
      }
    },
    sideDrawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      height: '56px',
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0',
      paddingRight: '8px'
    },
    imageUploaderContainer: {
      position: 'relative',
      maxHeight: '454px'
    },
    imageUploadLoader: {
      position: 'relative',
      height: '454px',
      top: '-400px',
      background: gray[800]
    },
    errMsg: {
      color: '#FF6060',
      fontSize: '12px'
    },
    sideDrawerLinkStyles: {
      color: gray[300],
      ':visited': {
        color: gray[300]
      }
    },
    sideDrawerSelectedLink: {
      color: blue[500]
    },
    sectionHeading: {
      // color: blue[200],
      color: 'white',
      fontSize: '14px',
      fontWeight: 'bold'
    },
    popupMenuContainer: {
      background: '#083050',
      width: '359px',
      height: '200px',
      maxHeight: '200px',
      overflowY: 'auto',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)'
    },
    popupMenuItem: {
      height: '60px',
      background: blue[700],
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      cursor: 'pointer',
      ':hover': {
        color: blue[200],
        background: blue[900]
      }
    },
    menuItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '60px',
      minHeight: '60px',
      width: '40px',
      minWidth: '40px'
    },
    menuItemIcon: {
      minWidth: '32px'
    },
    criticalSmallText: {
      fontSize: '12px',
      color: salmon[500],
      paddingLeft: '8px',
      fontWeight: 'bold',
      width: '140px'
    },
    whiteText: {
      color: 'white'
    },
    drawerBannerCameraIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[500],
      color: 'white',
      position: 'absolute',
      right: '0',
      bottom: '8px',
      fontSize: '11px',
      width: '24px',
      height: '24px',
      cursor: 'pointer',
      borderRadius: '50%'
    }
  });

  // #region Primary Insurance Drawer Event Handlers
  const openSecondaryInsuranceDrawer = () => {
    setShowInsurnaceDrawer(true);
    setSelectedPayerCopy(clone(selectedPayer));
    setPayerSearch('');
    setIsSaved(true);
    setMemberDataCopy({
      memberFirstName,
      memberMiddleName,
      memberLastName,
      memberDobDate,
      memberDobMonth,
      memberDobYear,
      isMemberDobPristine,
      patientRelationToMember,
      policyNumber,
      authorizationNumber,
      groupNumber,
      siFrontImageUrl,
      siBackImageUrl
    });
  };

  const closeSecondaryInsuranceDrawer = () => {
    setShowInsurnaceDrawer(false);
  };

  const handleOnChangePayerSearch = (e) => {
    const { value } = e.target;
    setPayerSearch(value);
    // setIsSaved(false);
  };

  const handleOnEditMemberFirstName = (e) => {
    const { value } = e.target;
    // setMemberFirstName(value);
    setMemberDataCopy(prvState => ({
      ...prvState,
      memberFirstName: value
    }));
    setIsSaved(false);
  };

  const handleOnEditMemberMiddleName = (e) => {
    const { value } = e.target;
    // setMemberMiddleName(value);
    setMemberDataCopy(prvState => ({
      ...prvState,
      memberMiddleName: value
    }));
    setIsSaved(false);
  };

  const handleOnEditMemberLastName = (e) => {
    const { value } = e.target;
    // setMemberLastName(value);
    setMemberDataCopy(prvState => ({
      ...prvState,
      memberLastName: value
    }));
    setIsSaved(false);
  };

  const handleOnEditPolicyNumber = (e) => {
    const { value } = e.target;
    // setPolicyNumber(value);
    setMemberDataCopy(prvState => ({
      ...prvState,
      policyNumber: value
    }));
    setIsSaved(false);
  };

  const handleOnEditAuthorizationNumber = (e) => {
    const { value } = e.target;
    // setAuthorizationNumber(value);
    setMemberDataCopy(prvState => ({
      ...prvState,
      authorizationNumber: value
    }));
    setIsSaved(false);
  };

  const handleOnEditGroupNumber = (e) => {
    const { value } = e.target;
    // setGroupNumber(value);
    setMemberDataCopy(prvState => ({
      ...prvState,
      groupNumber: value
    }));
    setIsSaved(false);
  };

  const handleOnClickSaveSiData = () => {
    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('payerId', selectedPayerCopy._id);
    formData.append('memberFirstName', memberDataCopy.memberFirstName || '');
    formData.append('memberMiddleName', memberDataCopy.memberMiddleName || '');
    formData.append('memberLastName', memberDataCopy.memberLastName || '');
    if (!memberDataCopy.isMemberDobPristine) {
      formData.append('memberDob', `${memberDataCopy.memberDobMonth + 1}/${memberDataCopy.memberDobDate}/${memberDataCopy.memberDobYear}`);
    } else {
      formData.append('memberDob', 'n/a');
    }
    // formData.append('memberDob', `${memberDataCopy.memberDobMonth + 1}/${memberDataCopy.memberDobDate}/${memberDataCopy.memberDobYear}`);
    formData.append('patientRelationToMember', memberDataCopy.patientRelationToMember);
    formData.append('policyNumber', memberDataCopy.policyNumber || '');
    formData.append('authorizationNumber', memberDataCopy.authorizationNumber || '');
    formData.append('groupNumber', memberDataCopy.groupNumber || '');

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/savePatientSecondaryInsuranceData`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Secondary Insurance data saved!');
          closeSecondaryInsuranceDrawer();
          setSelectedPayer(clone(selectedPayerCopy));
          setMemberFirstName(memberDataCopy.memberFirstName || '');
          setMemberMiddleName(memberDataCopy.memberMiddletName || '');
          setMemberLastName(memberDataCopy.memberLastName || '');
          setIsMemberDobPristine(memberDataCopy.isMemberDobPristine);
          setMemberDobDate(memberDataCopy.memberDobDate);
          setMemberDobMonth(memberDataCopy.memberDobMonth);
          setMemberDobYear(memberDataCopy.memberDobYear);
          setPatientRelationToMember(memberDataCopy.patientRelationToMember);
          setPolicyNumber(memberDataCopy.policyNumber || '');
          setAuthorizationNumber(memberDataCopy.authorizationNumber || '');
          setGroupNumber(memberDataCopy.groupNumber || '');
          setSiFrontImageUrl(memberDataCopy.siFrontImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
          setSiBackImageUrl(memberDataCopy.siBackImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
          props.updateFlag();
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnChangeIsNoSecondaryInsurance = () => {
    const updatedIsNoSiValue = !isNoSecondaryInsurance;
    setIsNoSecondaryInsurance(updatedIsNoSiValue);
    const formData = new FormData();
    formData.append('patientId', patientId);
    formData.append('isNoSecondaryInsurance', updatedIsNoSiValue);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/setNoSecondaryInsurance`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          // setIsNoPrimaryInsurance(res.isNoPrimaryInsurance);
          props.updateFlag();
        }
      })
      .catch(error => console.log('error', error));
  };

  const onDeleteSecondaryInsurance = () => {
    const formData = new FormData();
    formData.append('patientId', patientId);
    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
      // headers: reqHeaders
    };

    fetch(`${BASE_URL}/patients/deleteSecondaryInsurance`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
        if (res.success) {
          setShowPayerDeleteWarning(false);
          setShowInsurnaceDrawer(false);
          setSelectedPayer({});
          setSelectedPayerCopy({});
          setMemberFirstName('');
          setMemberMiddleName('');
          setMemberLastName('');
          setIsMemberDobPristine(true);
          setMemberDobDate(TODAY.getDate());
          setMemberDobMonth(TODAY.getMonth());
          setMemberDobYear(TODAY.getFullYear());
          setPatientRelationToMember('');
          setPolicyNumber('');
          setAuthorizationNumber('');
          setGroupNumber('');
          setShowRelationMenu('');
          setSelectedPayer({});
          setSiFrontImageUrl('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
          setSiBackImageUrl('https://quantumtrader.s3.amazonaws.com/default_insurance.jpg');
          setMemberDataCopy({
            memberFirstName: '',
            memberMiddleName: '',
            memberLastName: '',
            patientRelationToMember: '',
            memberDobDate: TODAY.getDate(),
            memberDobMonth: TODAY.getMonth(),
            memberDobYear: TODAY.getFullYear(),
            isMemberDobPristine: true,
            policyNumber: '',
            authorizationNumber: '',
            groupNumber: '',
            siFrontImageUrl: 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg',
            siBackImageUrl: 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'
          });
          props.updateFlag();
        }
      })
      .catch(error => console.log('error', error));
  };
  // #endregion

  // #region Front Cropper Drawer Event Handlers
  const closeFrontCropperDrawer = () => {
    toggleFrontCropperDrawer(false);
    setShowFrontCropper(false);
    setIsFrontUploading(false);
  };

  const uploadFront = () => {
    const formData = new FormData();
    const img = frontFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading avatar file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('SI Front file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          // setPiFrontImageUrl(res.fileUrl);
          setMemberDataCopy(prvState => ({
            ...prvState,
            siFrontImageUrl: res.fileUrl
          }));
          setIsFrontUploading(false);
          setShowFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnFrontFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsFrontUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading front primary insurance card.');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('SI Card front for cropper uploaded!');
        console.log(res);
        if (res.success) {
          // setPiFrontImageUrl(res.fileUrl);
          setMemberDataCopy(prvState => ({
            ...prvState,
            siFrontImageUrl: res.fileUrl
          }));
          setIsFrontUploading(false);
          setShowFrontCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedFront = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientId);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updateSiCardFront`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped front image url', res.fileUrl);
          // setPiFrontImageUrl(res.fileUrl);
          setMemberDataCopy(prvState => ({
            ...prvState,
            siFrontImageUrl: res.fileUrl
          }));
          setShowFrontLoader(false);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnFrontFileSelected = () => {
    if (frontFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', frontFileInputRef.current.files[0]);
      console.log('typeof img', typeof frontFileInputRef.current.files[0]);
      setIsFrontUploading(true);
      uploadFront();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickFrontCrop = () => {
    const imageElement = frontCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      frontCardRef.current.src = url;
      setShowFrontLoader(true);
      uploadCroppedFront(blob);
      closeFrontCropperDrawer();
    });
  };
  // #endregion

  // #region Back Cropper Drawer Event Handlers
  const closeBackCropperDrawer = () => {
    toggleBackCropperDrawer(false);
    setShowBackCropper(false);
    setIsBackUploading(false);
  };

  const uploadBack = () => {
    const formData = new FormData();
    const img = backFileInputRef.current.files[0];
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading PI Card (back) file for cropper');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('SI Card (back) file for cropper uploaded!');
        console.log(res);
        if (res.success) {
          // setPiBackImageUrl(res.fileUrl);
          setMemberDataCopy(prvState => ({
            ...prvState,
            siBackImageUrl: res.fileUrl
          }));
          setIsBackUploading(false);
          setShowBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBackFileDrop = (img) => {
    const formData = new FormData();
    formData.append('file', img);
    const options = { method: 'POST', body: formData, redirect: 'follow' };
    setIsBackUploading(true);
    fetch(`${BASE_URL}/users/uploadImg`, options)
      .then(response => {
        console.log('Uploading back primary insurance card.');
        console.log(response);
        return response.text();
      })
      .then(result => {
        const res = JSON.parse(result);
        console.log('SI Card back for cropper uploaded!');
        console.log(res);
        if (res.success) {
          // setPiBackImageUrl(res.fileUrl);
          setMemberDataCopy(prvState => ({
            ...prvState,
            siBackImageUrl: res.fileUrl
          }));
          setIsBackUploading(false);
          setShowBackCropper(true);
        }
      })
      .catch(error => console.log('error', error));
  };

  const uploadCroppedBack = (blob) => {
    const headers = new Headers();
    const token = localStorage.getItem('token');
    headers.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('file', blob);
    formData.append('patientId', patientId);
    const requestOptions = { method: 'POST', headers, body: formData, redirect: 'follow' };

    fetch(`${BASE_URL}/patients/updateSiCardBack`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          console.log('Cropped back image url', res.fileUrl);
          // setPiBackImageUrl(res.fileUrl);
          setMemberDataCopy(prvState => ({
            ...prvState,
            siBackImageUrl: res.fileUrl
          }));
          setShowBackLoader(false);
        }
      })
      .catch(error => console.log('error', error));
  };

  const handleOnBackFileSelected = () => {
    if (backFileInputRef.current.files.length > 0) {
      console.log('Selected file = ', backFileInputRef.current.files[0]);
      console.log('typeof img', typeof backFileInputRef.current.files[0]);
      setIsBackUploading(true);
      uploadBack();
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnClickBackCrop = () => {
    const imageElement = backCropperRef.current;
    const { cropper } = imageElement;
    cropper.getCroppedCanvas().toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      backCardRef.current.src = url;
      setShowBackLoader(true);
      uploadCroppedBack(blob);
      closeBackCropperDrawer();
    });
  };
  // #endregion

  return (
    <div
      className='pl-2 pr-2 pt-4'
      style={{ height: 'calc(100% - 140px)' }}
    >
      {/* Primary Insurance Drawer */}
      {showInsurnaceDrawer &&
        <Drawer
          placement="right"
          visible={showInsurnaceDrawer}
          toggleDrawer={() => setShowDrawerCloseWarning(true)}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          {/* Drawer Container */}
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div className='fx fx__hv-center'>
                {/* <i className='mr-2 fa fa-lg fa-user' /> */}
                {siIcon}
                <div className='ml-2'>Secondary Insurance</div>
              </div>
              <div className='fx fx__v-center'>
                {!isSaved
                  ? <Button
                      btnType='primary'
                      isDisabled={showFrontLoader || showBackLoader}
                      onClick={handleOnClickSaveSiData}
                      style={{
                        width: '90px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                      }}
                    >
                      Save Changes
                    </Button>
                  : <div style={{ color: blue[500] }}>
                      <i className='fa fa-check mr-2' />
                      Saved
                    </div>
                }
                {!isSaved
                  ? <i
                      className={`ml-2 fa fa-lg fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                      onClick={() => setShowDrawerCloseWarning(true)}
                    />
                  : <div
                      className='fx fx__hv-center ml-3'
                      style={{
                        width: '24px',
                        height: '24px',
                        background: blue[200],
                        color: 'black',
                        fontSize: '20px'
                      }}
                      onClick={() => closeSecondaryInsuranceDrawer()}
                    >
                      <i
                        className='fa fa-compress'
                        onClick={() => closeSecondaryInsuranceDrawer()}
                      />
                    </div>
                }
              </div>
            </div>
            {/* Content */}
            <div className={css(styles.sideDrawerContentArea)}>
              <div
                ref={payerSearchMenuRef}
                style={{
                  position: 'relative',
                  display: Object.keys(selectedPayerCopy).length === 0 ? '' : 'none'
                }}
              >
                <TextInputWithLabel
                  value={payerSearch}
                  autoFocus
                  showClearFieldIcon
                  type='email'
                  titleText="Search Payers"
                  isCompactLayout={false}
                  titleColor="#5079ab"
                  width={359}
                  showPreIcon
                  preIcon="fa fa-search"
                  iconSize={16}
                  onChange={handleOnChangePayerSearch}
                  onClick={() => {
                    if (payerSearchResults.length > 0 && payerSearch.length > 0) {
                      setShowPayerSearchMenu(true);
                    }
                  }}
                />
                <PopupMenu
                  top={45}
                  right={1}
                  visible={showPayerSearchMenu}
                  parent={payerSearchMenuRef}
                  position="absolute"
                  onOutsideClick={() => { setShowPayerSearchMenu(false); }}
                >
                  <div className={css(styles.popupMenuContainer)}>
                    {payerSearchResults.map((el) => (
                      <div
                        key={el._id}
                        className={css(styles.popupMenuItem)}
                        onClick={() => {
                          console.log(el);
                          setSelectedPayerCopy(el);
                          setPayerSearch('');
                          setIsSaved(false);
                          // setSelectedPayer(el);
                        }}
                      >
                        <div className={css(styles.menuItemIconContainer)}>
                          <svg
                            width="32"
                            height="32"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M0 4a4 4 0 0 1 4-4h24a4 4 0 0 1 4 4v24a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Zm16.096-1.334L4.668 6.476v11.428c0 6.313 5.116 11.429 11.428 11.429 6.313 0 11.43-5.116 11.43-11.429V6.476l-11.43-3.81Zm.002 24.526a9.286 9.286 0 0 0 9.286-9.286V8.084l-9.286-3.273-9.285 3.273v9.822a9.286 9.286 0 0 0 9.285 9.286Z"
                              fill="#96C8FF"
                            />
                          </svg>
                        </div>
                        <div
                          className='ml-2'
                          style={{ fontSize: '12px' }}
                        >
                          <div>{el.payerName}</div>
                          {el.userName && el.userName.length > 0 &&
                            <div style={{ color: blue[500] }}>
                              @{el.userName}
                            </div>
                          }
                        </div>
                      </div>
                    ))}
                  </div>
                </PopupMenu>
              </div>
              {/* Payer Card */}
              <PayerCard
                selectedPayer={selectedPayerCopy}
                onDelete={() => setShowPayerDeleteWarning(true)}
              />
              {/* Member relation to patient, firstName, middleName, lastName, dob etc */}
              <div className='mt-4'>
                {Object.keys(selectedPayerCopy).length > 0 &&
                  <div className='fx mt-4'>
                    <div
                      className='mr-2 fx'
                      ref={relationRef}
                      style={{ position: 'relative' }}
                    >
                      Patient Relation To Subscriber:&nbsp;
                      <div
                        style={{
                          color: memberDataCopy.patientRelationToMember === '' ? salmon[500] : blue[500],
                          cursor: 'pointer'
                        }}
                        onClick={() => setShowRelationMenu(true)}
                      >
                        {/* {patientRelationToMember === '' ? 'Required' : patientRelationToMember}&nbsp; */}
                        {memberDataCopy.patientRelationToMember === '' ? 'Required' : memberDataCopy.patientRelationToMember}&nbsp;
                        <i className='fa fa-caret-down' />
                      </div>
                      <PopupMenu
                        top={22}
                        right={1}
                        visible={showRelationMenu}
                        parent={relationRef}
                        position="absolute"
                        onOutsideClick={() => { setShowRelationMenu(false); }}
                      >
                        <div
                          className={css(styles.popupMenuContainer)}
                          style={{ width: '150px', height: '224px', maxHeight: '224px' }}
                        >
                          {/* Self */}
                          <div
                            className={css(styles.popupMenuItem)}
                            style={{
                              height: '32px',
                              padding: '8px',
                              borderLeft: memberDataCopy.patientRelationToMember === 'Self' ? `2px solid ${blue[500]}` : '',
                              background: memberDataCopy.patientRelationToMember === 'Self' ? blue[800] : ''
                            }}
                            onClick={() => {
                              setShowRelationMenu(false);
                              const tmpDate = new Date(props.patientData.dob);
                              setMemberDataCopy(prvState => ({
                                ...prvState,
                                patientRelationToMember: 'Self',
                                memberFirstName: props.patientData.firstName,
                                memberMiddleName: props.patientData.middleName,
                                memberLastName: props.patientData.lastName,
                                memberDobDate: tmpDate.getDate(),
                                memberDobMonth: tmpDate.getMonth(),
                                memberDobYear: tmpDate.getFullYear(),
                                isMemberDobPristine: false
                              }));
                              setIsSaved(false);
                            }}
                          >
                            <div style={{ fontSize: '12px' }}>Self</div>
                          </div>
                          {/* Spouse */}
                          <div
                            className={css(styles.popupMenuItem)}
                            style={{
                              height: '32px',
                              padding: '8px',
                              borderLeft: memberDataCopy.patientRelationToMember === 'Spouse' ? `2px solid ${blue[500]}` : '',
                              background: memberDataCopy.patientRelationToMember === 'Spouse' ? blue[800] : ''
                            }}
                            onClick={() => {
                              setShowRelationMenu(false);
                              // setPatientRelationToMember('Spouse');
                              setMemberDataCopy(prvState => ({
                                ...prvState,
                                memberFirstName: '',
                                memberMiddleName: '',
                                memberLastName: '',
                                isMemberDobPristine: true,
                                memberDobDate: TODAY.getDate(),
                                memberDobMonth: TODAY.getMonth(),
                                memberDobYear: TODAY.getFullYear(),
                                patientRelationToMember: 'Spouse'
                              }));
                              setIsSaved(false);
                            }}
                          >
                            <div style={{ fontSize: '12px' }}>Spouse</div>
                          </div>
                          {/* Parent */}
                          <div
                            className={css(styles.popupMenuItem)}
                            style={{
                              height: '32px',
                              padding: '8px',
                              borderLeft: memberDataCopy.patientRelationToMember === 'Parent' ? `2px solid ${blue[500]}` : '',
                              background: memberDataCopy.patientRelationToMember === 'Parent' ? blue[800] : ''
                            }}
                            onClick={() => {
                              setShowRelationMenu(false);
                              // setPatientRelationToMember('Parent');
                              setMemberDataCopy(prvState => ({
                                ...prvState,
                                memberFirstName: '',
                                memberMiddleName: '',
                                memberLastName: '',
                                isMemberDobPristine: true,
                                memberDobDate: TODAY.getDate(),
                                memberDobMonth: TODAY.getMonth(),
                                memberDobYear: TODAY.getFullYear(),
                                patientRelationToMember: 'Parent'
                              }));
                              setIsSaved(false);
                            }}
                          >
                            <div style={{ fontSize: '12px' }}>Parent</div>
                          </div>
                          {/* Legal Guardian */}
                          <div
                            className={css(styles.popupMenuItem)}
                            style={{
                              height: '32px',
                              padding: '8px',
                              borderLeft: memberDataCopy.patientRelationToMember === 'Legal Guardian' ? `2px solid ${blue[500]}` : '',
                              background: memberDataCopy.patientRelationToMember === 'Legal Guardian' ? blue[800] : ''
                            }}
                            onClick={() => {
                              setShowRelationMenu(false);
                              // setPatientRelationToMember('Legal Guardian');
                              setMemberDataCopy(prvState => ({
                                ...prvState,
                                memberFirstName: '',
                                memberMiddleName: '',
                                memberLastName: '',
                                isMemberDobPristine: true,
                                memberDobDate: TODAY.getDate(),
                                memberDobMonth: TODAY.getMonth(),
                                memberDobYear: TODAY.getFullYear(),
                                patientRelationToMember: 'Legal Guardian'
                              }));
                              setIsSaved(false);
                            }}
                          >
                            <div style={{ fontSize: '12px' }}>Legal Guardian</div>
                          </div>
                          {/* Child */}
                          <div
                            className={css(styles.popupMenuItem)}
                            style={{
                              height: '32px',
                              padding: '8px',
                              borderLeft: memberDataCopy.patientRelationToMember === 'Child' ? `2px solid ${blue[500]}` : '',
                              background: memberDataCopy.patientRelationToMember === 'Child' ? blue[800] : ''
                            }}
                            onClick={() => {
                              setShowRelationMenu(false);
                              // setPatientRelationToMember('Child');
                              setMemberDataCopy(prvState => ({
                                ...prvState,
                                memberFirstName: '',
                                memberMiddleName: '',
                                memberLastName: '',
                                isMemberDobPristine: true,
                                memberDobDate: TODAY.getDate(),
                                memberDobMonth: TODAY.getMonth(),
                                memberDobYear: TODAY.getFullYear(),
                                patientRelationToMember: 'Child'
                              }));
                              setIsSaved(false);
                            }}
                          >
                            <div style={{ fontSize: '12px' }}>Child</div>
                          </div>
                          {/* Sibling */}
                          <div
                            className={css(styles.popupMenuItem)}
                            style={{
                              height: '32px',
                              padding: '8px',
                              borderLeft: memberDataCopy.patientRelationToMember === 'Sibling' ? `2px solid ${blue[500]}` : '',
                              background: memberDataCopy.patientRelationToMember === 'Sibling' ? blue[800] : ''
                            }}
                            onClick={() => {
                              setShowRelationMenu(false);
                              // setPatientRelationToMember('Sibling');
                              setMemberDataCopy(prvState => ({
                                ...prvState,
                                memberFirstName: '',
                                memberMiddleName: '',
                                memberLastName: '',
                                isMemberDobPristine: true,
                                memberDobDate: TODAY.getDate(),
                                memberDobMonth: TODAY.getMonth(),
                                memberDobYear: TODAY.getFullYear(),
                                patientRelationToMember: 'Sibling'
                              }));
                              setIsSaved(false);
                            }}
                          >
                            <div style={{ fontSize: '12px' }}>Sibling</div>
                          </div>
                          {/* Other */}
                          <div
                            className={css(styles.popupMenuItem)}
                            style={{
                              height: '32px',
                              padding: '8px',
                              borderLeft: memberDataCopy.patientRelationToMember === 'Other' ? `2px solid ${blue[500]}` : '',
                              background: memberDataCopy.patientRelationToMember === 'Other' ? blue[800] : ''
                            }}
                            onClick={() => {
                              setShowRelationMenu(false);
                              setMemberDataCopy(prvState => ({
                                ...prvState,
                                memberFirstName: '',
                                memberMiddleName: '',
                                memberLastName: '',
                                isMemberDobPristine: true,
                                memberDobDate: TODAY.getDate(),
                                memberDobMonth: TODAY.getMonth(),
                                memberDobYear: TODAY.getFullYear(),
                                patientRelationToMember: 'Other'
                              }));
                              setIsSaved(false);
                            }}
                          >
                            <div style={{ fontSize: '12px' }}>Other</div>
                          </div>
                        </div>
                      </PopupMenu>
                    </div>
                  </div>
                }
                {/* <div className={css(styles.sectionHeading)}>
                  Member/Subscriber (May or may not be patient)
                </div> */}
                {(memberDataCopy.patientRelationToMember && memberDataCopy.patientRelationToMember.length > 0) &&
                  <>
                    <div className='fx mt-2'>
                      <div>
                        <TextInputWithLabel
                          value={memberDataCopy.memberFirstName || ''}
                          titleText="First Name"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={109}
                          placeholder='Required'
                          onChange={handleOnEditMemberFirstName}
                          isDisabled={memberDataCopy.patientRelationToMember === 'Self'}
                        />
                      </div>
                      <div className='ml-2'>
                        <TextInputWithLabel
                          value={memberDataCopy.memberMiddleName || ''}
                          titleText="Middle Name"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={109}
                          onChange={handleOnEditMemberMiddleName}
                          isDisabled={memberDataCopy.patientRelationToMember === 'Self'}
                        />
                      </div>
                      <div className='ml-2'>
                        <TextInputWithLabel
                          value={memberDataCopy.memberLastName || ''}
                          titleText="Last Name"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={109}
                          placeholder='Required'
                          onChange={handleOnEditMemberLastName}
                          isDisabled={memberDataCopy.patientRelationToMember === 'Self'}
                        />
                      </div>
                    </div>
                    <div className='fx mt-4'>
                      <div className='mr-2'>
                        <i className='fa fa-calendar mr-2' />
                        Subscriber DOB
                      </div>
                      {/* eslint-disable */}
                      <DatePicker
                        left={-120}
                        right={0}
                        date={memberDataCopy.memberDobDate || TODAY.getDate()}
                        // month={memberDataCopy.memberDobMonth || TODAY.getMonth()}
                        month={memberDataCopy.hasOwnProperty('memberDobMonth') ? memberDataCopy.memberDobMonth : TODAY.getMonth()}
                        year={memberDataCopy.memberDobYear || TODAY.getFullYear()}
                        // isPrisitine={memberDataCopy.isMemberDobPristine || true}
                        isPrisitine={memberDataCopy.isMemberDobPristine}
                        color={blue[400]}
                        noUnderline
                        isDisabled={memberDataCopy.patientRelationToMember === 'Self'}
                        onChangeDate={(year, month, date) => {
                          setMemberDataCopy(prvState => ({
                            ...prvState,
                            memberDobDate: date,
                            memberDobMonth: month,
                            memberDobYear: year,
                            isMemberDobPristine: false
                          }));
                          setIsSaved(false);
                          // setMemberDobDate(date);
                          // setMemberDobMonth(month);
                          // setMemberDobYear(year);
                          // setIsMemberDobPristine(false);
                        }}
                      />
                      {/* eslint-enable */}
                    </div>
                    <div className='fx mt-2'>
                      <div>
                        <TextInputWithLabel
                          value={memberDataCopy.policyNumber || ''}
                          titleText="Policy #"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={167}
                          placeholder='Required'
                          onChange={handleOnEditPolicyNumber}
                        />
                      </div>
                      <div className='ml-2'>
                        <TextInputWithLabel
                          value={memberDataCopy.authorizationNumber || ''}
                          titleText="Authorization #"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={167}
                          placeholder='Required'
                          onChange={handleOnEditAuthorizationNumber}
                        />
                      </div>
                    </div>
                    <div className='fx mt-2'>
                      <div>
                        <TextInputWithLabel
                          value={memberDataCopy.groupNumber || ''}
                          titleText="Group #"
                          titleFontSize={12}
                          isCompactLayout={true}
                          titleColor={gray[200]}
                          textColor="white"
                          selectionColor="white"
                          selectionBackground="#0080FF"
                          spellcheck={false}
                          width={167}
                          placeholder='Required'
                          onChange={handleOnEditGroupNumber}
                        />
                      </div>
                    </div>
                  </>
                }
              </div>

              {(memberDataCopy.patientRelationToMember && memberDataCopy.patientRelationToMember.length > 0) &&
                <>
                  {/* Secondary Insurnace Card - Front */}
                  <div className='mt-4'>
                    <div className='fx'>
                      {siIcon}
                      <div className='ml-2'>
                        Insurance Card (Front)
                      </div>
                    </div>
                    {showFrontLoader
                      ? <Loader iconType="cog" iconSize="3x" text="Uploading Insurance ID(Front)" />
                      : <div style={{ position: 'relative' }}>
                        <img
                          className='mt-2'
                          ref={frontCardRef}
                          src={memberDataCopy.siFrontImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'}
                          style={{
                            width: '343px',
                            maxWidth: '343px',
                            height: 'auto'
                          }}
                        />
                        <div
                          onClick={() => toggleFrontCropperDrawer(true)}
                          className={css(styles.drawerBannerCameraIcon)}
                        >
                          <i className="fa fa-camera" />
                        </div>
                      </div>
                    }
                  </div>
                  {/* Secondary Insurnace Card - Back */}
                  <div className='mt-4'>
                    <div className='fx'>
                      {siIcon}
                      <div className='ml-2'>
                        Insurance Card (Back)
                      </div>
                    </div>
                    {showBackLoader
                      ? <Loader iconType="cog" iconSize="3x" text="Uploading Insurance ID(Back)" />
                      : <div style={{ position: 'relative' }}>
                        <img
                          className='mt-2'
                          ref={backCardRef}
                          src={memberDataCopy.siBackImageUrl || 'https://quantumtrader.s3.amazonaws.com/default_insurance.jpg'}
                          style={{
                            width: '343px',
                            maxWidth: '343px',
                            height: 'auto'
                          }}
                        />
                        <div
                          onClick={() => toggleBackCropperDrawer(true)}
                          className={css(styles.drawerBannerCameraIcon)}
                        >
                          <i className="fa fa-camera" />
                        </div>
                      </div>
                    }
                  </div>
                </>
              }
            </div>
          </div>
        </Drawer>
      }
      {/* Primary Insurance Card (Front) Cropper Drawer */}
      {showFrontCropperDrawer &&
        <Drawer
          placement="right"
          visible={showFrontCropperDrawer}
          toggleDrawer={closeFrontCropperDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Secondary Insurance Card (Front)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={closeFrontCropperDrawer}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showFrontCropper
                ? <div>
                  <Cropper
                    src={memberDataCopy.siFrontImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={frontCropperRef}
                  />
                </div>
                : null
              }
              {!showFrontCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={frontFileInputRef}
                    onDrop={handleOnFrontFileDrop}
                    onChange={handleOnFrontFileSelected}
                  />
                </div>
                : null
              }
              {isFrontUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Secondary Insurance (Front) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickFrontCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }
      {/* Primary Insurance Card (Back) Cropper Drawer */}
      {showBackCropperDrawer &&
        <Drawer
          placement="right"
          visible={showBackCropperDrawer}
          toggleDrawer={closeBackCropperDrawer}
          zIndex="2000"
          width={window.innerWidth <= 700 ? window.innerWidth : 375}
        >
          <div className={css(styles.sideDrawerContainer)}>
            {/* Header */}
            <div className={css(styles.sideDrawerHeader)}>
              <div>Upload Secondary Insurance Card (Back)</div>
              <i
                className={`fa fa-times-circle ${css(styles.sideDrawerHeaderCloseIcon)}`}
                onClick={closeBackCropperDrawer}
              />
            </div>
            {/* Content Area */}
            <div className={css(styles.sideDrawerContentArea, styles.imageUploaderContainer)}>
              {showBackCropper
                ? <div>
                  <Cropper
                    src={memberDataCopy.siBackImageUrl}
                    style={{ height: 400, width: '100%' }}
                    checkCrossOrigin
                    checkOrientation={false}
                    background={false}
                    viewMode={1}
                    autoCropArea={1}
                    guides={false}
                    ref={backCropperRef}
                  />
                </div>
                : null
              }
              {!showBackCropper
                ? <div>
                  <FileUploader
                    title='Select File'
                    parentRef={backFileInputRef}
                    onDrop={handleOnBackFileDrop}
                    onChange={handleOnBackFileSelected}
                  />
                </div>
                : null
              }
              {isBackUploading
                ? <div className={css(styles.imageUploadLoader)}>
                  <Loader iconType="cog" iconSize="5x" text="Uploading Secondary Insurance (Back) Image" />
                </div>
                : null
              }
            </div>
            {/* Drawer footer area */}
            <div
              className={`fx fx__hv-center ${css(styles.sideDrawerFooter)}`}
              style={{ position: 'absolute', bottom: '0' }}
            >
              <Button
                btnType="primary"
                className="fx fx__hv-center"
                onClick={handleOnClickBackCrop}
                style={{
                  height: '32px',
                  width: '340px',
                  padding: '3px 0'
                }}
              >
                Crop Image
              </Button>
            </div>
          </div>
        </Drawer>
      }

      {/* Drawer close warning dialog */}
      {showDrawerCloseWarning &&
        <WarningDialog
          show={showDrawerCloseWarning}
          title='Close Secondary Insurance Drawer'
          message='Do you want to close without saving?'
          onCancel={() => setShowDrawerCloseWarning(false)}
          onContinue={() => {
            setShowDrawerCloseWarning(false);
            closeSecondaryInsuranceDrawer();
          }}
        />
      }

      {/* Delete payer warning dialog */}
      {showPayerDeleteWarning &&
        <WarningDialog
          show={showPayerDeleteWarning}
          title='Delete Payer'
          message='Do you want to delete payer?'
          onCancel={() => setShowPayerDeleteWarning(false)}
          onContinue={onDeleteSecondaryInsurance}
        />
      }

      {Object.keys(selectedPayer).length > 0
        ? <div
          style={{
            height: 'calc(100% - 10px)',
            overflowY: 'auto'
          }}
        >
          <div style={{ width: '359px' }}>
            <PayerCard
              selectedPayer={selectedPayer}
              onDelete={() => setSelectedPayer({})}
              showEllipsis
              onClickOnEllipsis={openSecondaryInsuranceDrawer}
            />
          </div>
          <div className='fx mt-4'>
            <div>Member/Subscriber:</div>
            <div className={`ml-2 ${css(styles.whiteText)}`}>
              {memberFirstName && memberFirstName.length > 0 && memberLastName && memberLastName.length > 0
                ? `${titleCase(memberFirstName)}${memberMiddleName && memberMiddleName.length > 0 ? ` ${titleCase(memberMiddleName)}` : ''} ${titleCase(memberLastName)}`
                : <div style={{ fontSize: '14px', color: salmon[500] }}>Required</div>
              }
              {/* {`${memberFirstName}${memberMiddleName && memberMiddleName.length > 0 ? ` ${memberMiddleName}` : ''} ${memberLastName}`} */}
            </div>
          </div>
          <div className='fx mt-2'>
            <div>Subscriber DOB:</div>
            <div className={`ml-2 ${css(styles.whiteText)}`}>
              {!isMemberDobPristine
                ? <div>{`${memberDobMonth + 1}/${memberDobDate}/${memberDobYear}`}</div>
                : <div style={{ fontSize: '14px', color: salmon[500] }}>Required</div>
              }
              {/* {`${memberDobMonth + 1}/${memberDobDate}/${memberDobYear}`} */}
            </div>
          </div>
          <div className='fx mt-2'>
            <div>Patient Relation To Subscriber:</div>
            <div className={`ml-2 ${css(styles.whiteText)}`}>
              {patientRelationToMember && patientRelationToMember !== ''
                ? <div>{patientRelationToMember}</div>
                : <div style={{ fontSize: '14px', color: salmon[500] }}>
                  Required
                </div>
              }
            </div>
          </div>
          <div className='fx mt-2'>
            <div>Policy #:
              <span className={`ml-2 ${css(styles.whiteText)}`}>
                {policyNumber && policyNumber !== ''
                  ? <span>{policyNumber}</span>
                  : <span style={{ fontSize: '14px', color: salmon[500] }}>Required</span>
                }
              </span>
            </div>
            <div className='ml-2'>
              Authorization #:
              <span className={`ml-2 ${css(styles.whiteText)}`}>
                {authorizationNumber && authorizationNumber !== ''
                  ? <span>{authorizationNumber}</span>
                  : <span style={{ fontSize: '14px', color: salmon[500] }}>Required</span>
                }
              </span>
            </div>
          </div>
          <div className='fx mt-2'>
            <div>Group #:</div>
            <div className={`ml-2 ${css(styles.whiteText)}`}>
              {groupNumber && groupNumber !== ''
                ? <div>{groupNumber}</div>
                : <div style={{ fontSize: '14px', color: salmon[500] }}>
                  Required
                </div>
              }
            </div>
          </div>
          {/* Secondary Insurnace Card - Front */}
          <div className='mt-4'>
            <div className='fx'>
              {siIcon}
              <div className='ml-2'>
                Insurance Card (Front)
              </div>
            </div>
            <img
              className='mt-2'
              src={siFrontImageUrl}
              style={{
                width: '343px',
                maxWidth: '343px',
                height: 'auto'
              }}
            />
          </div>
          {/* Primary Insurnace Card - Back */}
          <div className='mt-4'>
            <div className='fx'>
              {siIcon}
              <div className='ml-2'>
                Insurance Card (Back)
              </div>
            </div>
            <img
              className='mt-2'
              src={siBackImageUrl}
              style={{
                width: '343px',
                maxWidth: '343px',
                height: 'auto'
              }}
            />
          </div>
        </div>
        : <>
          <Button
            btnType='primary'
            isDisabled={isNoSecondaryInsurance}
            onClick={openSecondaryInsuranceDrawer}
            style={{
              width: '375px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            className={`${isNoSecondaryInsurance ? 'disabled' : ''} ${css(styles.btnIcon)}`}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <svg
                width="18"
                height="20"
                fill="inherit"
                xmlns="http://www.w3.org/2000/svg"
                className='mr-2'
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M.5 2.857 9.071 0l8.572 2.857v8.572A8.57 8.57 0 0 1 9.07 20 8.57 8.57 0 0 1 .5 11.429V2.857Zm8.574 15.536a6.965 6.965 0 0 0 6.964-6.964V4.063L9.074 1.607 2.109 4.063v7.366a6.965 6.965 0 0 0 6.965 6.964Zm1.101-5.318V14h-4v-.925H7.64V8.86c0-.167.005-.338.015-.515l-1.04.87a.457.457 0 0 1-.18.09.457.457 0 0 1-.3-.05.318.318 0 0 1-.09-.085l-.39-.535L7.86 6.76h1.015v6.315h1.3Zm2.957-8.095a2.222 2.222 0 0 0-.828-.148c-.307 0-.585.05-.836.148-.25.099-.465.239-.644.42a1.906 1.906 0 0 0-.412.664 2.526 2.526 0 0 0-.144.876c0 .325.048.619.144.88.099.261.236.484.412.668.179.184.393.325.644.424.25.099.53.148.836.148.304 0 .58-.05.828-.148.25-.099.464-.24.64-.424a1.87 1.87 0 0 0 .412-.668c.096-.261.144-.555.144-.88 0-.323-.048-.615-.144-.876a1.794 1.794 0 0 0-1.052-1.084Zm-.072 2.976c-.163.23-.415.344-.756.344-.347 0-.604-.115-.772-.344-.165-.232-.248-.568-.248-1.008 0-.44.083-.776.248-1.008.168-.235.425-.352.772-.352.341 0 .593.116.756.348.165.232.248.57.248 1.012 0 .443-.083.779-.248 1.008Z"
                  style={{ fill: 'inherit' }}
                />
              </svg>
              <div>Add Secondary Insurnace</div>
            </div>
          </Button>
          <div className='mt-2'>
            <Checkbox
              text='Check if no Secondary Insurance'
              isChecked={isNoSecondaryInsurance}
              onChange={() => {
                handleOnChangeIsNoSecondaryInsurance();
              }}
            />
          </div>
        </>
      }
    </div>
  );
};

export default SecondaryInsurance;
